import React, { useState } from 'react';
import {
  Card,
  CardContent,
  Box,
  Typography,
  IconButton,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
  Chip,
  Alert,
  CircularProgress,
  Tooltip,
  Checkbox,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from '@mui/material';
import {
  PictureAsPdf,
  Image,
  AttachFile,
  MoreVert,
  Download,
  Delete,
  Warning,
  SelectAll,
  DeleteSweep,
  Visibility,
} from '@mui/icons-material';
import { DOCUMENT_TYPE_LABELS } from '../../services/leads';
import { useSelector } from 'react-redux';
import { hasPermission } from '../../utils/permissions';

const DocumentViewer = ({
  documents = [],
  onDownload,
  onDelete,
  onBulkDelete,
  leadId,
  loading = false,
}) => {
  // Ensure documents is always an array at component level
  const safeDocuments = Array.isArray(documents) ? documents : [];

  // Log warning if documents is not an array
  if (!Array.isArray(documents) && documents !== undefined) {
    console.warn('DocumentViewer received non-array documents prop:', documents, typeof documents);
  }
  const [anchorEl, setAnchorEl] = useState(null);
  const [selectedDoc, setSelectedDoc] = useState(null);
  const [downloadStates, setDownloadStates] = useState({});
  const [selectedDocuments, setSelectedDocuments] = useState(new Set());
  const [bulkDeleteDialogOpen, setBulkDeleteDialogOpen] = useState(false);
  const [bulkSelectMode, setBulkSelectMode] = useState(false);
  const { user } = useSelector(state => state.auth);

  const handleMenuOpen = (event, doc) => {
    setAnchorEl(event.currentTarget);
    setSelectedDoc(doc);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
    setSelectedDoc(null);
  };

  const handleDownload = async (doc) => {
    setDownloadStates(prev => ({ ...prev, [doc.id]: 'loading' }));

    try {
      const response = await onDownload(doc.id);

      if (response.download_url) {
        if (response.is_s3_signed) {
          // S3 signed URL - programmatic download
          const link = document.createElement('a');
          link.href = response.download_url;
          link.download = response.filename || doc.file_name || 'document';
          link.target = '_blank';
          document.body.appendChild(link);
          link.click();
          document.body.removeChild(link);
        } else if (response.is_blob) {
          // Local storage - handle blob URL (fallback)
          const link = document.createElement('a');
          link.href = response.download_url;
          link.download = response.filename || doc.file_name || 'document';
          document.body.appendChild(link);
          link.click();
          document.body.removeChild(link);
          window.URL.revokeObjectURL(response.download_url);
        } else {
          // Default - open in new tab
          window.open(response.download_url, '_blank');
        }
        setDownloadStates(prev => ({ ...prev, [doc.id]: 'success' }));
      } else {
        console.warn('No download URL provided in response:', response);
        setDownloadStates(prev => ({ ...prev, [doc.id]: 'error' }));
      }
    } catch (error) {
      console.error('Download failed:', error);
      setDownloadStates(prev => ({ ...prev, [doc.id]: 'error' }));
    } finally {
      setTimeout(() => {
        setDownloadStates(prev => ({ ...prev, [doc.id]: null }));
      }, 3000);
    }

    handleMenuClose();
  };

  const handleView = async (doc) => {
    try {
      // Always use secure download endpoint for viewing
      const response = await onDownload(doc.id);
      if (response.download_url) {
        window.open(response.download_url, '_blank');
      } else {
        console.error('No secure URL available for viewing document');
        alert('Unable to view document. Please try again or contact support.');
      }
    } catch (error) {
      console.error('Failed to get secure URL for viewing:', error);
      alert('Unable to view document. Please try again or contact support.');
    }
    handleMenuClose();
  };

  const handleDelete = async (doc) => {
    if (window.confirm(`Are you sure you want to delete "${doc.file_name}"?\n\nThis action cannot be undone.`)) {
      try {
        await onDelete(doc.id);
        // Show success message or handle success state
        console.log(`Successfully deleted document: ${doc.file_name}`);
      } catch (error) {
        console.error('Delete failed:', error);
        alert('Failed to delete document. Please try again.');
      }
    }
    handleMenuClose();
  };

  const handleDocumentSelect = (docId) => {
    const newSelected = new Set(selectedDocuments);
    if (newSelected.has(docId)) {
      newSelected.delete(docId);
    } else {
      newSelected.add(docId);
    }
    setSelectedDocuments(newSelected);
  };

  const handleSelectAll = () => {
    const allDocIds = safeDocuments.map(doc => doc.id);
    setSelectedDocuments(new Set(allDocIds));
  };

  const handleDeselectAll = () => {
    setSelectedDocuments(new Set());
  };

  const handleBulkDelete = () => {
    setBulkDeleteDialogOpen(true);
  };

  const confirmBulkDelete = async () => {
    try {
      const documentIds = Array.from(selectedDocuments);
      await onBulkDelete(documentIds);
      setSelectedDocuments(new Set());
      setBulkDeleteDialogOpen(false);
      setBulkSelectMode(false);
    } catch (error) {
      console.error('Bulk delete failed:', error);
    }
  };

  const toggleBulkSelectMode = () => {
    setBulkSelectMode(!bulkSelectMode);
    if (bulkSelectMode) {
      setSelectedDocuments(new Set());
    }
  };

  const getFileIcon = (fileName) => {
    const extension = fileName?.split('.').pop()?.toLowerCase();
    if (extension === 'pdf') return <PictureAsPdf color="error" />;
    if (['jpg', 'jpeg', 'png', 'gif'].includes(extension)) return <Image color="primary" />;
    return <AttachFile color="action" />;
  };

  const formatFileSize = (bytes) => {
    if (!bytes) return 'Unknown size';
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  const canDownload = (doc) => {
    if (!user || !doc) {
      console.log('DocumentViewer: No user or doc for download check', { user: !!user, doc: !!doc });
      return false;
    }

    // Debug log to understand the document structure
    console.log('DocumentViewer: Checking download permission', {
      userRole: user.role,
      userId: user.id,
      docId: doc.id,
      docLead: doc.lead,
      userAssignedStates: user.assigned_states,
      userAssignedDiseases: user.assigned_diseases
    });

    // Admin can download everything
    if (user.role === 'admin') return true;

    // For managers, check if they have access to the lead's state
    if (user.role === 'manager') {
      if (doc.lead?.state) {
        return user.assigned_states?.includes(doc.lead.state);
      }
      // If no lead state info, allow download for managers
      return true;
    }

    // For team leads, check state or disease access
    if (user.role === 'team_lead') {
      let hasAccess = false;

      if (doc.lead?.state && user.assigned_states?.includes(doc.lead.state)) {
        hasAccess = true;
      }

      if (doc.lead?.disease && user.assigned_diseases?.includes(doc.lead.disease)) {
        hasAccess = true;
      }

      // If no lead info available, allow access for team leads
      if (!doc.lead) {
        hasAccess = true;
      }

      return hasAccess;
    }

    // For BDAs, check if they're assigned to the lead
    if (user.role === 'bda') {
      if (doc.lead?.assigned_to) {
        return doc.lead.assigned_to === user.id;
      }
      // If no assignment info, allow download for BDAs
      return true;
    }

    return false;
  };

  const canDelete = (doc) => {
    return hasPermission(user.role, 'leads.edit') && canDownload(doc);
  };

  const getDocumentsByType = () => {
    if (!Array.isArray(safeDocuments)) {
      console.warn('DocumentViewer: safeDocuments is not an array:', safeDocuments);
      return {};
    }
    return safeDocuments.reduce((acc, doc) => {
      const type = doc.document_type || 'others';
      if (!acc[type]) {
        acc[type] = [];
      }
      acc[type].push(doc);
      return acc;
    }, {});
  };

  const documentsByType = getDocumentsByType();

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (!safeDocuments.length) {
    return (
      <Alert severity="info">
        No documents uploaded for this lead yet.
      </Alert>
    );
  }

  return (
    <Box>
      {/* Bulk Actions Header */}
      {safeDocuments.length > 0 && (
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <Button
              variant={bulkSelectMode ? "contained" : "outlined"}
              startIcon={<SelectAll />}
              onClick={toggleBulkSelectMode}
              size="small"
            >
              {bulkSelectMode ? 'Exit Select Mode' : 'Select Multiple'}
            </Button>

            {bulkSelectMode && (
              <>
                <Button
                  variant="outlined"
                  onClick={handleSelectAll}
                  disabled={selectedDocuments.size === safeDocuments.length}
                  size="small"
                >
                  Select All
                </Button>
                <Button
                  variant="outlined"
                  onClick={handleDeselectAll}
                  disabled={selectedDocuments.size === 0}
                  size="small"
                >
                  Deselect All
                </Button>
              </>
            )}
          </Box>

          {bulkSelectMode && selectedDocuments.size > 0 && (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Chip
                label={`${selectedDocuments.size} selected`}
                color="primary"
                size="small"
              />
              <Button
                variant="contained"
                color="error"
                startIcon={<DeleteSweep />}
                onClick={handleBulkDelete}
                size="small"
              >
                Delete Selected
              </Button>
            </Box>
          )}
        </Box>
      )}

      {Object.entries(DOCUMENT_TYPE_LABELS).map(([type, label]) => {
        const docs = documentsByType[type] || [];
        if (docs.length === 0) return null;

        return (
          <Box key={type} sx={{ mb: 3 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
              <Typography variant="h6" sx={{ flex: 1 }}>
                {label}
              </Typography>
              <Chip label={docs.length} size="small" />
            </Box>

            {docs.map((doc) => (
              <Card
                key={doc.id}
                variant="outlined"
                sx={{
                  mb: 2,
                  ...(bulkSelectMode && selectedDocuments.has(doc.id) && {
                    borderColor: 'primary.main',
                    backgroundColor: 'primary.50',
                  })
                }}
              >
                <CardContent>
                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flex: 1 }}>
                      {bulkSelectMode && (
                        <Checkbox
                          checked={selectedDocuments.has(doc.id)}
                          onChange={() => handleDocumentSelect(doc.id)}
                          disabled={!canDelete(doc)}
                        />
                      )}
                      {getFileIcon(doc.file_name)}
                      <Box sx={{ flex: 1, minWidth: 0 }}>
                        <Typography variant="body1" noWrap fontWeight="medium">
                          {doc.file_name}
                        </Typography>
                        <Typography variant="body2" color="text.secondary" noWrap>
                          {doc.description}
                        </Typography>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mt: 1 }}>
                          <Typography variant="caption" color="text.secondary">
                            {formatFileSize(doc.file_size)}
                          </Typography>
                          {doc.uploaded_by_name && (
                            <Typography variant="caption" color="text.secondary">
                              Uploaded by: {doc.uploaded_by_name}
                            </Typography>
                          )}
                          {doc.uploaded_at && (
                            <Typography variant="caption" color="text.secondary">
                              {new Date(doc.uploaded_at).toLocaleDateString()}
                            </Typography>
                          )}
                        </Box>
                      </Box>
                    </Box>

                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      {downloadStates[doc.id] === 'loading' && (
                        <CircularProgress size={20} />
                      )}
                      {downloadStates[doc.id] === 'success' && (
                        <Chip label="Downloaded" color="success" size="small" />
                      )}
                      {downloadStates[doc.id] === 'error' && (
                        <Chip label="Error" color="error" size="small" />
                      )}

                      {!bulkSelectMode && (
                        <>
                          {!canDownload(doc) ? (
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                              <Tooltip title={`No permission to access this document. Your role: ${user?.role}. Contact admin if you need access.`}>
                                <IconButton disabled>
                                  <Warning color="warning" />
                                </IconButton>
                              </Tooltip>
                              <Typography variant="body2" color="text.secondary">
                                No access permission
                              </Typography>
                            </Box>
                          ) : (
                            <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', alignItems: 'center' }}>
                              <Button
                                variant="outlined"
                                size="small"
                                startIcon={<Visibility />}
                                onClick={() => handleView(doc)}
                                disabled={downloadStates[doc.id] === 'loading'}
                              >
                                View
                              </Button>

                              <Button
                                variant="contained"
                                size="small"
                                startIcon={<Download />}
                                onClick={() => handleDownload(doc)}
                                disabled={downloadStates[doc.id] === 'loading'}
                                color="primary"
                              >
                                Download
                              </Button>

                              {canDelete(doc) && (
                                <Button
                                  variant="outlined"
                                  size="small"
                                  startIcon={<Delete />}
                                  onClick={() => handleDelete(doc)}
                                  color="error"
                                  disabled={downloadStates[doc.id] === 'loading'}
                                >
                                  Delete
                                </Button>
                              )}
                            </Box>
                          )}
                        </>
                      )}
                    </Box>
                  </Box>
                </CardContent>
              </Card>
            ))}
          </Box>
        );
      })}

      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleMenuClose}
      >
        <MenuItem onClick={() => handleView(selectedDoc)}>
          <ListItemIcon>
            <Visibility fontSize="small" />
          </ListItemIcon>
          <ListItemText>View</ListItemText>
        </MenuItem>

        <MenuItem onClick={() => handleDownload(selectedDoc)}>
          <ListItemIcon>
            <Download fontSize="small" />
          </ListItemIcon>
          <ListItemText>Download</ListItemText>
        </MenuItem>

        {canDelete(selectedDoc) && (
          <MenuItem onClick={() => handleDelete(selectedDoc)} sx={{ color: 'error.main' }}>
            <ListItemIcon>
              <Delete fontSize="small" color="error" />
            </ListItemIcon>
            <ListItemText>Delete</ListItemText>
          </MenuItem>
        )}
      </Menu>

      {/* Bulk Delete Confirmation Dialog */}
      <Dialog open={bulkDeleteDialogOpen} onClose={() => setBulkDeleteDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>
          Confirm Bulk Delete
        </DialogTitle>
        <DialogContent>
          <Alert severity="warning" sx={{ mb: 2 }}>
            Are you sure you want to delete {selectedDocuments.size} selected document(s)? This action cannot be undone.
          </Alert>
          <Typography variant="body2" color="text.secondary">
            Selected documents:
          </Typography>
          <Box sx={{ mt: 1, maxHeight: 200, overflowY: 'auto' }}>
            {Array.from(selectedDocuments).map(docId => {
              const doc = safeDocuments.find(d => d.id === docId);
              return doc ? (
                <Box key={docId} sx={{ display: 'flex', alignItems: 'center', gap: 1, py: 0.5 }}>
                  {getFileIcon(doc.file_name)}
                  <Typography variant="body2">{doc.file_name}</Typography>
                </Box>
              ) : null;
            })}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setBulkDeleteDialogOpen(false)}>Cancel</Button>
          <Button
            variant="contained"
            color="error"
            onClick={confirmBulkDelete}
            startIcon={<DeleteSweep />}
          >
            Delete {selectedDocuments.size} Document(s)
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default DocumentViewer;